import {
    AgendaConsultant,
    AgendaEventStatus,
    AgendaPatient,
    ConsultantAbsenceReason,
    EventCategory,
    EventRoom,
    EventType
} from '@/features/agenda/types'
import {TeamMember} from '@/features/treatment-requests/teams/types'
import i18n from '@/translations/i18n'
import {UploadedDocument} from '@/types'
import {z} from 'zod'
import {Access} from '../accesses/types'
import {DayOfWeekEnum} from '@/features/users-availability/types'
import {validateAccessTypeCompatibility} from './utils'

// Fields shared between all event types
export const AgendaEventFieldsSchema = z.object({
    id: z.string(),
    type: EventType,
    isGenericEvent: z.boolean().default(false),
    note: z.string().nullish(),
    category: EventCategory.nullable(),
    status: AgendaEventStatus.nullable(),
    isGroupTreatment: z.boolean(),
    consultants: z
        .array(
            AgendaConsultant.refine(consultant => !!consultant.accessType, {
                message: i18n.t('errors:required'),
                path: ['accessType']
            })
        )
        .min(1, {message: i18n.t('errors:required')})
        .max(2, {message: i18n.t('agenda:max_consultants', {count: 2})}),
    patients: z
        .array(AgendaPatient)
        .min(1, {message: i18n.t('errors:required')})
        .max(5, {message: i18n.t('agenda:max_patients', {count: 5})}),
    eventTeamMembers: z.array(TeamMember.pick({id: true, group: true, user: true})),
    date: z.object({
        date: z.date({
            required_error: i18n.t('errors:required'),
            invalid_type_error: i18n.t('errors:required')
        }),
        time: z.string({required_error: i18n.t('errors:required')}).min(1, {message: i18n.t('errors:required')}),
        location: z.enum(EventRoom.options, {required_error: i18n.t('errors:required')})
    }),
    duration: z
        .string({required_error: i18n.t('errors:required')})
        .min(1, {message: i18n.t('errors:required')})
        .nullable(),
    isRemote: z.coerce.boolean().nullish(),
    remoteUrl: z
        .string()
        .url({message: i18n.t('errors:invalid_link')})
        .nullish(),
    isARecovery: z.coerce.boolean().nullish(),
    communicatedOnTime: z.enum(['within_48_hours', 'over_48_hours']).nullable(),
    communicatedAttachemnts: UploadedDocument.nullish(),
    startDate: z.date({required_error: i18n.t('errors:required')}),
    endDate: z.date({required_error: i18n.t('errors:required')}),
    startTime: z.string({required_error: i18n.t('errors:required')}).optional(),
    endTime: z.string({required_error: i18n.t('errors:required')}).optional(),
    absenceReason: z.enum(ConsultantAbsenceReason.options, {required_error: i18n.t('errors:required')}),
    absenceTime: z.boolean().optional(),
    consultant: AgendaConsultant.merge(
        z.object({
            specializationName: z.string().optional()
        })
    ).optional(),
    substitute: AgendaConsultant.shape['substitute'].optional(),
    notificationsRecipients: z.array(z.object({id: z.string().min(1), fullName: z.string()})).optional(),
    notificationDetails: z.string().optional(),
    missedAccess: Access.omit({medicalRecordReport: true}).optional(),
    isRepeated: z.boolean().optional(),
    repetitionData: z
        .object({
            frequency: z.enum(['weekly', 'monthly']).optional(),
            position: z.number().optional(),
            dayOfWeek: z.enum(DayOfWeekEnum.options).optional()
        })
        .optional()
})
export type AgendaEventFieldsSchema = z.infer<typeof AgendaEventFieldsSchema>

// Event fields shown in the all events form view (it excludes absence events)
const AgendaEventSchema = z.discriminatedUnion('type', [
    z.object({type: z.literal('event')}).merge(
        AgendaEventFieldsSchema.pick({
            consultants: true,
            patients: true,
            status: true,
            isGroupTreatment: true,
            date: true,
            duration: true,
            isRemote: true,
            remoteUrl: true,
            isARecovery: true,
            missedAccess: true
        })
    )
])
export type AgendaEventSchema = z.infer<typeof AgendaEventSchema>

// Event categories (it excludes absence events)
const AgendaEventSingleSchema = z.discriminatedUnion('category', [
    z
        .object({
            category: z.enum(['evaluations', 'specialist_visits', 'project_management'])
        })
        .merge(
            AgendaEventFieldsSchema.pick({
                isRemote: true,
                remoteUrl: true
            })
        )
])
export type AgendaEventSingleSchema = z.infer<typeof AgendaEventSingleSchema>

// Treatment event categories
const AgendaEventTreatmentSchema = z.discriminatedUnion('category', [
    z.object({category: z.literal('treatments')}).merge(
        AgendaEventFieldsSchema.pick({
            isRemote: true,
            remoteUrl: true,
            isRepeated: true,
            repetitionData: true
        })
    )
])
export type AgendaEventTreatmentSchema = z.infer<typeof AgendaEventTreatmentSchema>

// Team meeting event categories
const AgendaEventTeamMeetingSchema = z.discriminatedUnion('category', [
    z.object({category: z.literal('team_meeting')}).merge(
        AgendaEventFieldsSchema.pick({
            eventTeamMembers: true,
            isRemote: true,
            remoteUrl: true
        })
    )
])

// Base schema for create event form - all fields that might be used
const CreateEventFormBaseSchema = z.object({
    isGenericEvent: z.boolean(),
    type: EventType,
    note: z.string().nullish(),
    // Non-generic event fields (optional at base level, validated in superRefine)
    category: EventCategory.nullable().optional(),
    status: AgendaEventStatus.nullable().optional(),
    isGroupTreatment: z.boolean().optional(),
    consultants: z.array(AgendaConsultant).optional(),
    patients: z.array(AgendaPatient).optional(),
    eventTeamMembers: z.array(TeamMember.pick({id: true, group: true, user: true})).optional(),
    date: z
        .object({
            date: z.date().optional(),
            time: z.string().optional(),
            location: z.enum(EventRoom.options).optional()
        })
        .optional(),
    duration: z.string().nullable().optional(),
    isRemote: z.coerce.boolean().nullish(),
    remoteUrl: z
        .string()
        .url({message: i18n.t('errors:invalid_link')})
        .nullish(),
    isARecovery: z.coerce.boolean().nullish(),
    missedAccess: Access.omit({medicalRecordReport: true}).optional(),
    isRepeated: z.boolean().optional(),
    repetitionData: z
        .object({
            frequency: z.enum(['weekly', 'monthly']).optional(),
            position: z.number().optional(),
            dayOfWeek: z.enum(DayOfWeekEnum.options).optional()
        })
        .optional()
})

// Create event form validation schema with conditional validation
export const CreateEventFormValidationSchema = CreateEventFormBaseSchema.superRefine((data, ctx) => {
    if (data.isGenericEvent) {
        // Generic event validation: consultants, date, time, duration
        if (!data.consultants || data.consultants.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['consultants']
            })
        } else if (data.consultants.length > 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('agenda:max_consultants', {count: 1}),
                path: ['consultants']
            })
        }

        if (!data.date?.date) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['date', 'date']
            })
        }

        if (!data.date?.time) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['date', 'time']
            })
        }

        if (!data.duration) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['duration']
            })
        }

        if (!data.note || data.note.trim() === '') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['note']
            })
        }
    } else {
        // Non-generic event validation
        // Category is required
        if (!data.category) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['category']
            })
        }

        // Consultants validation
        if (!data.consultants || data.consultants.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['consultants']
            })
        } else {
            if (data.consultants.length > 2) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: i18n.t('agenda:max_consultants', {count: 2}),
                    path: ['consultants']
                })
            }
            // Validate each consultant has accessType
            for (const [index, consultant] of data.consultants.entries()) {
                if (!consultant.accessType) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: i18n.t('errors:required'),
                        path: ['consultants', index, 'accessType']
                    })
                }
            }
        }

        // Patients validation
        if (!data.patients || data.patients.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['patients']
            })
        } else if (data.patients.length > 5) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('agenda:max_patients', {count: 5}),
                path: ['patients']
            })
        }

        // Date validation (with location for non-generic)
        if (!data.date?.date) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['date', 'date']
            })
        }

        if (!data.date?.time) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['date', 'time']
            })
        }

        if (!data.date?.location) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['date', 'location']
            })
        }

        // Duration validation
        if (!data.duration) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['duration']
            })
        }

        // Group treatment validation
        if (data.isGroupTreatment && data.patients && data.patients.length < 2) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('agenda:min_patients', {count: 2}),
                path: ['patients']
            })
        }

        // Recovery validation
        if (data.isARecovery && !data.missedAccess) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['missedAccess']
            })
        }

        // Access type compatibility validation
        if (data.patients && data.consultants) {
            for (const patient of data.patients) {
                const projectType = patient.project?.type
                for (const [consultantIndex, consultant] of data.consultants.entries()) {
                    const accessType = consultant.accessType?.accessType

                    if (validateAccessTypeCompatibility(projectType, accessType, data.status ?? null)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: i18n.t('agenda:incompatible_access_type'),
                            path: ['consultants', consultantIndex, 'accessType']
                        })
                    }
                }
            }
        }
    }
})
export type CreateEventFormValidationSchema = z.infer<typeof CreateEventFormValidationSchema>

export const CreateEventFormConfirmationValidationSchema = CreateEventFormValidationSchema.and(
    AgendaEventFieldsSchema.pick({
        notificationDetails: true,
        notificationsRecipients: true
    })
)
export type CreateEventFormConfirmationValidationSchema = z.infer<typeof CreateEventFormConfirmationValidationSchema>

// Edit event form validation schema (used in the agenda edit dialog)
export const EditEventFormValidationSchema = AgendaEventFieldsSchema.pick({
    id: true,
    type: true,
    note: true,
    substitute: true,
    remoteUrl: true
})
    .and(z.discriminatedUnion('type', [...AgendaEventSchema.options]))
    .and(
        z.discriminatedUnion('category', [
            ...AgendaEventSingleSchema.options,
            ...AgendaEventTeamMeetingSchema.options,
            ...AgendaEventTreatmentSchema.options
        ])
    )
    .superRefine((data, ctx) => {
        // Group treatment validation
        if (data.isGroupTreatment && data.patients.length < 2) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('agenda:min_patients', {count: 2}),
                path: ['patients']
            })
        }

        // Recovery validation
        if (data.isARecovery && !data.missedAccess) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('errors:required'),
                path: ['missedAccess']
            })
        }

        // Access type compatibility validation
        for (const patient of data.patients) {
            const projectType = patient.project?.type
            for (const [consultantIndex, consultant] of data.consultants.entries()) {
                const accessType = consultant.accessType?.accessType
                if (validateAccessTypeCompatibility(projectType, accessType, data.status)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: i18n.t('agenda:incompatible_access_type'),
                        path: ['consultants', consultantIndex, 'accessType']
                    })
                }
            }
        }
    })
export type EditEventFormValidationSchema = z.infer<typeof EditEventFormValidationSchema>

export const EditEventFormConfirmationValidationSchema = EditEventFormValidationSchema.and(
    AgendaEventFieldsSchema.pick({
        notificationDetails: true,
        notificationsRecipients: true
    })
)
export type EditEventFormConfirmationValidationSchema = z.infer<typeof EditEventFormConfirmationValidationSchema>

// Edit generic event form validation schema
export const EditGenericEventFormValidationSchema = z.object({
    id: z.string(),
    type: z.literal('generic_event'),
    note: z.string({required_error: i18n.t('errors:required')}).min(1, {message: i18n.t('errors:required')}),
    status: AgendaEventStatus,
    consultants: z
        .array(AgendaConsultant)
        .min(1, {message: i18n.t('errors:required')})
        .max(1),
    date: z.object({
        date: z.date({required_error: i18n.t('errors:required')}),
        time: z.string({required_error: i18n.t('errors:required')}).min(1, {message: i18n.t('errors:required')})
    }),
    duration: z.string({required_error: i18n.t('errors:required')}).min(1, {message: i18n.t('errors:required')})
})
export type EditGenericEventFormValidationSchema = z.infer<typeof EditGenericEventFormValidationSchema>

export const EditGenericEventFormConfirmationValidationSchema = EditGenericEventFormValidationSchema.and(
    AgendaEventFieldsSchema.pick({
        notificationDetails: true,
        notificationsRecipients: true
    })
)
export type EditGenericEventFormConfirmationValidationSchema = z.infer<
    typeof EditGenericEventFormConfirmationValidationSchema
>

// Quick edit event form validation schema (used in the agenda details dialog)
export const QuickEditEventFormValidationSchema = AgendaEventFieldsSchema.pick({
    id: true,
    status: true,
    consultants: true,
    patients: true,
    substitute: true
}).superRefine((data, ctx) => {
    // Access type compatibility validation
    for (const patient of data.patients) {
        const projectType = patient.project?.type
        for (const [consultantIndex, consultant] of data.consultants.entries()) {
            const accessType = consultant.accessType?.accessType
            if (validateAccessTypeCompatibility(projectType, accessType, data.status)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: i18n.t('agenda:incompatible_access_type'),
                    path: ['consultants', consultantIndex, 'accessType']
                })
            }
        }
    }
})
export type QuickEditEventFormValidationSchema = z.infer<typeof QuickEditEventFormValidationSchema>

export const AddPatientAbsenceConfirmationValidationSchema = AgendaEventFieldsSchema.pick({
    patients: true,
    communicatedOnTime: true,
    communicatedAttachemnts: true,
    startDate: true,
    endDate: true,
    notificationsRecipients: true,
    notificationDetails: true
})
export type AddPatientAbsenceConfirmationValidationSchema = z.infer<
    typeof AddPatientAbsenceConfirmationValidationSchema
>

export const AddPatientAbsenceFormValidationSchema = AgendaEventFieldsSchema.pick({
    patients: true,
    startDate: true,
    endDate: true,
    communicatedOnTime: true,
    communicatedAttachemnts: true
}).refine(
    data => !(data.communicatedOnTime && data.communicatedOnTime == 'within_48_hours' && !data.communicatedAttachemnts),
    {
        message: i18n.t('errors:required'),
        path: ['communicatedAttachemnts']
    }
)
export type AddPatientAbsenceFormValidationSchema = z.infer<typeof AddPatientAbsenceFormValidationSchema>

export const AddConsultantAbsenceFormValidationSchema = AgendaEventFieldsSchema.pick({
    consultant: true,
    startDate: true,
    endDate: true,
    startTime: true,
    endTime: true,
    absenceReason: true,
    absenceTime: true
}).superRefine((data, ctx) => {
    if (data.absenceTime && !data.startTime) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('errors:required'),
            path: ['startTime']
        })
    }

    if (data.absenceTime && !data.endTime) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('errors:required'),
            path: ['endTime']
        })
    }

    if (!data.consultant) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('errors:required'),
            path: ['consultant']
        })
    }
})

export type AddConsultantAbsenceFormValidationSchema = z.infer<typeof AddConsultantAbsenceFormValidationSchema>

export const AddConsultantAbsenceConfirmationValidationSchema = AgendaEventFieldsSchema.pick({
    consultant: true,
    startDate: true,
    endDate: true,
    startTime: true,
    endTime: true,
    absenceReason: true,
    absenceTime: true,
    notificationsRecipients: true,
    notificationDetails: true
})
export type AddConsultantAbsenceConfirmationValidationSchema = z.infer<
    typeof AddConsultantAbsenceConfirmationValidationSchema
>

export const DeleteAgendaEventFormValidationSchema = AgendaEventFieldsSchema.pick({
    notificationsRecipients: true,
    notificationDetails: true
})
export type DeleteAgendaEventFormValidationSchema = z.infer<typeof DeleteAgendaEventFormValidationSchema>
